import {
    EzString,
    EzArray
} from '/ezlibrary/helpers/EzHelpers.js';

import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

import { EzDateTime } from '/public/javascript/common/ez-date-time.js';

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * Defines available billing frequencies for subscription plans
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *      import {
 *          // ... other enumeration classes ...
 *          EzBillingFrequency
 *      } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other enumeration classes or classes that cannot
 * import EzEnumerations:
 *      import { EzBillingFrequency } from '/ezlibrary/enums/EzBillingFrequency.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access static reference:
 *      Inside class: EzBillingFrequency.{method or property}
 *      Outside of class: EzBillingFrequency.{method or property}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzBillingFrequency extends EzEnumeration2 {
    /**
     * @static
     * @public @method
     * Gets the billing frequency.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {number}
     * The billing frequency
     */
    static getBillingFrequency(enumValue = 'UNKNOWN') {
        return this.ezEnumData(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN))
            .billingFrequency;
    }

    /**
     * @static
     * @public @method
     * Gets the display name.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {string}
     * the display name
     */
    static getDisplayName(enumValue = 'UNKNOWN') {
        return this.ezEnumData(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN))
            .displayName;
    }

    /**
     * @static
     * @public @method
     * Checks if is auto billed.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {boolean}
     * True, if is auto billed
     */
    static isAutoBilled(enumValue = 'UNKNOWN') {
        return this.ezEnumData(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN))
            .autoBilled;
    }

    /**
     * @static
     * @public @method
     * Checks if is daily billing frequency.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {boolean}
     * True, if is daily billing frequency
     */
    static isDailyBillingFrequency(enumValue = 'UNKNOWN') {
        return 365 === this.getBillingFrequency(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN));
    }

    /**
     * @static
     * @public @method
     * Checks if is monthly billing frequency.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {boolean}
     * True, if is monthly billing frequency
     */
    static isMonthlyBillingFrequency(enumValue = 'UNKNOWN') {
        return 12 === this.getBillingFrequency(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN));
    }

    /**
     * @static
     * @public @method
     * Checks if is one time billing frequency.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {boolean}
     * True, if is one time billing frequency
     */
    static isOneTimeBillingFrequency(enumValue = 'UNKNOWN') {
        return 1 === this.getBillingFrequency(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN));
    }

    /**
     * @static
     * @public @method
     * Checks if is quarterly billing frequency.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {boolean}
     * True, if is quarterly billing frequency
     */
    static isQuarterlyBillingFrequency(enumValue = 'UNKNOWN') {
        return 4 === this.getBillingFrequency(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN));
    }

    /**
     * @static
     * @public @method
     * Checks if is support defined billing frequency.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {boolean}
     * True, if is support defined billing frequency
     */
    static isSupportDefinedBillingFrequency(enumValue = 'UNKNOWN') {
        return 0 === this.getBillingFrequency(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN));
    }

    /**
     * @static
     * @public @method
     * Checks if is weekly billing frequency.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {boolean}
     * True, if is weekly billing frequency
     */
    static isWeeklyBillingFrequency(enumValue = 'UNKNOWN') {
        return 52 === this.getBillingFrequency(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN));
    }

    /**
     * @static
     * @public @method
     * Checks if is yearly billing frequency.
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN
     *
     * @return {boolean}
     * True, if is yearly billing frequency
     */
    static isYearlyBillingFrequency(enumValue = 'UNKNOWN') {
        return 2023 === this.getBillingFrequency(
            EzString.stringWithTextOrDefault(
                enumValue,
                EzBillingFrequency.UNKNOWN));
    }

    /**
     * @static
     * @public @method
     * Calculates the estimated billing date time for the billing frequency
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.UNKNOWN;
     * @param {number} lastBilledMoment
     * the last billed date time
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNow()
     *
     * @return the billing dates
     */
    static getEstimatedBillingDates(enumValue = 'UNKNOWN', lastBilledMoment = null) {
        const enumValueToUse = EzString.stringWithTextOrDefault(
            enumValue,
            EzBillingFrequency.UNKNOWN);

        const firstBilledMoment = globalThis.ezApi.ezclocker.ezDateTime.ezIsValidMoment(lastBilledMoment)
            ? EzDateTime.ezInstance.ezMomentWithPTZ(lastBilledMoment)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNow();

        if (enumValueToUse === EzBillingFrequency.UNKNOWN) {
            return EzArray.EMPTY;
        }

        if (this.isOneTimeBillingFrequency(enumValueToUse)) {
            return [
                firstBilledMoment
            ];
        }

        if (this.isYearlyBillingFrequency(enumValueToUse)) {
            return EzBillingFrequency.calculateYearlyBillingDates(firstBilledMoment);
        }

        if (this.isQuarterlyBillingFrequency(enumValueToUse)) {
            return EzBillingFrequency.calculateQuarterlyBillingDates(firstBilledMoment);
        }

        if (this.isMonthlyBillingFrequency(enumValueToUse)) {
            return EzBillingFrequency.calculateMonthlyBillingDates(firstBilledMoment);
        }

        if (this.isWeeklyBillingFrequency(enumValueToUse)) {
            return EzBillingFrequency.calculateWeeklyBillingDates(firstBilledMoment);
        }

        if (this.isDailyBillingFrequency(enumValueToUse)) {
            return EzBillingFrequency.calculateDailingBillingDates(firstBilledMoment);
        }

        // Cannot determine billing frequency for enumValue
        return EzBillingFrequency.calculateSupportBillingFrequency(
            enumValueToUse,
            firstBilledMoment);
    }

    /**
     * @static
     * @public @method
     * Calculates the billing dates for a support billing frequency starting from the provided lastBilledMoment
     *
     * @param {string} enumValue
     * A valid enumeration property value from EzBillingFrequency
     * Default: EzBillingFrequency.SUPPORT;
     * @param {undefined|null|moment} lastBilledMoment
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNow();
     *
     * @returns {array}
     */
    static calculateSupportBillingFrequency(enumValue = 'SUPPORT', lastBilledMoment = null) {
        const firstBilledMoment = globalThis.ezApi.ezclocker.ezDateTime.ezIsValidMoment(lastBilledMoment)
            ? EzDateTime.ezInstance.ezMomentWithPTZ(lastBilledMoment)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNow();

        switch (enumValue) {
            case EzBillingFrequency.SUPPORT_YEARLY:
                return EzBillingFrequency.calculateYearlyBillingDates(firstBilledMoment);
            case EzBillingFrequency.SUPPORT_QUARTERLY:
                return EzBillingFrequency.calculateQuarterlyBillingDates(firstBilledMoment);
            case EzBillingFrequency.SUPPORT_MONTHLY:
                return EzBillingFrequency.calculateMonthlyBillingDates(firstBilledMoment);
            case EzBillingFrequency.SUPPORT_WEEKLY:
                return EzBillingFrequency.calculateWeeklyBillingDates(firstBilledMoment);
            case EzBillingFrequency.SUPPORT_DAILY:
                return EzBillingFrequency.calculateDailingBillingFrequency(firstBilledMoment);
            case EzBillingFrequency.SUPPORT_ONE_TIME:
                return [
                    firstBilledMoment
                ];
            case EzBillingFrequency.SUPPORT:
            default:
                return EzArray.EMPTY;
        }
    }

    /**
     * @static
     * @public @method
     * Calculates the yearly billing date starting from the provided lastBilledMoment
     *
     * @param {undefined|null|moment} lastBilledMoment
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNow();
     *
     * @returns {array}
     */
    static calculateYearlyBillingDates(lastBilledMoment = null) {
        const firstBilledMoment = globalThis.ezApi.ezclocker.ezDateTime.ezIsValidMoment(lastBilledMoment)
            ? EzDateTime.ezInstance.ezMomentWithPTZ(lastBilledMoment)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNow();

        const billingDates = [
            firstBilledMoment
        ];

        billingDates.push(
            EzDateTime.ezInstance.ezAdd(
                firstBilledMoment,
                'years',
                1));

        return billingDates;
    }

    /**
     * @static
     * @public @method
     * Calculates the quarterly billing dates for a year starting from the provided lastBilledMoment
     *
     * @param {undefined|null|moment} lastBilledMoment
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNow();
     *
     * @returns {array}
     */
    static calculateQuarterlyBillingDates(lastBilledMoment = null) {
        const firstBilledMoment = globalThis.ezApi.ezclocker.ezDateTime.ezIsValidMoment(lastBilledMoment)
            ? EzDateTime.ezInstance.ezMomentWithPTZ(lastBilledMoment)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNow();

        const billingDates = [
            firstBilledMoment
        ];

        billingDates.push(
            EzDateTime.ezInstance.ezAdd(
                firstBilledMoment,
                'months',
                3));

        billingDates.push(
            EzDateTime.ezInstance.ezAdd(
                firstBilledMoment,
                'months',
                6));

        billingDates.push(
            EzDateTime.ezInstance.ezAdd(
                firstBilledMoment,
                'months',
                9));

        billingDates.push(
            EzDateTime.ezInstance.ezAdd(
                firstBilledMoment,
                'months',
                12));

        return billingDates;
    }

    /**
     * @static
     * @public @method
     * Calculates the monthly billing dates for a year starting from the provided lastBilledMoment
     *
     * @param {undefined|null|moment} lastBilledMoment
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNow();
     *
     * @returns {array}
     */
    static calculateMonthlyBillingDates(lastBilledMoment = null) {
        const firstBilledMoment = globalThis.ezApi.ezclocker.ezDateTime.ezIsValidMoment(lastBilledMoment)
            ? EzDateTime.ezInstance.ezMomentWithPTZ(lastBilledMoment)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNow();

        const billingDates = [
            firstBilledMoment
        ];

        for (let months = 1; months <= 12; months++) {
            billingDates.add(
                EzDateTime.ezInstance.ezAdd(
                    firstBilledMoment,
                    'months',
                    months));
        }

        return billingDates;
    }

    /**
     * @static
     * @public @method
     * Calculates the weekly billing frequency dates for a year starting from the provided lastBilledMoment.
     *
     * @param {undefined|null|moment} lastBilledMoment
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNow();
     *
     * @returns {array}
     */
    static calculateWeeklyBillingDates(lastBilledMoment = null) {
        const firstBilledMoment = globalThis.ezApi.ezclocker.ezDateTime.ezIsValidMoment(lastBilledMoment)
            ? EzDateTime.ezInstance.ezMomentWithPTZ(lastBilledMoment)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNow();

        const billingDates = [
            firstBilledMoment
        ];

        for (let weeks = 1; weeks <= 52; weeks++) {
            billingDates.add(
                EzDateTime.ezInstance.ezAdd(
                    firstBilledMoment,
                    'weeks',
                    weeks));
        }

        return billingDates;
    }

    /**
     * @static
     * @public @method
     * Calculates the dailing billing frequency dates for a year starting from the provided lastBilledMoment.
     *
     * @param {undefined|null|moment} lastBilledMoment
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNow();
     *
     * @returns {array}
     */
    static calculateDailingBillingFrequency(lastBilledMoment = null) {
        const firstBilledMoment = globalThis.ezApi.ezclocker.ezDateTime.ezIsValidMoment(lastBilledMoment)
            ? EzDateTime.ezInstance.ezMomentWithPTZ(lastBilledMoment)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNow();

        const billingDates = [
            firstBilledMoment
        ];

        for (let days = 1; days <= 365; days++) {
            billingDates.add(
                EzDateTime.ezInstance.ezAdd(
                    firstBilledMoment,
                    'days',
                    days));
        }

        return billingDates;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     *
     * @type {EzBillingFrequency}
     */
    static #ezEnumerationSingleton = null;

    /**
     * @static
     * Static Initialization of Enumeration
     */
    static {
        if (null == this.#ezEnumerationSingleton) {
            EzEnumeration2.ezWaitReady(
                EzDateTime.ezEventNames.onReady,
                () => {
                    this.#ezEnumerationSingleton = new EzBillingFrequency(
                        // Enum property names
                        [
                            'UNKNOWN',
                            'NONE',
                            'YEARLY',
                            'QUARTERLY',
                            'MONTHLY',
                            'WEEKLY',
                            'DAILY',
                            'ONE_TIME',
                            'SUPPORT_YEARLY',
                            'SUPPORT_QUARTERLY',
                            'SUPPORT_MONTHLY',
                            'SUPPORT_WEEKLY',
                            'SUPPORT_DAILY',
                            'SUPPORT_ONE_TIME',
                            'SUPPORT'
                        ],
                        // Enum property values for each enum property name as an array
                        // Enter null if the values are equal to the names
                        null,
                        // Optional array of additional data for eac enumeration property.
                        // Enter null if not needed.
                        [
                            // UNKNOWN
                            {
                                displayName: 'Support will contact when payment needs made.',
                                billingFrequency: 0,
                                autoBilled: false
                            },
                            {
                                displayName: 'No charge.',
                                billingFrequency: 0,
                                autoBilled: false
                            },
                            // YEARLY
                            {
                                displayName: 'Automatic Yearly Charge',
                                billingFrequency: 2023,
                                autoBilled: true
                            },
                            // QUARTERLY
                            {
                                displayName: 'Automatic Quarterly Charge',
                                billingFrequency: 4,
                                autoBilled: true
                            },
                            // MONTHLY
                            {
                                displayName: 'Automatic Monthly Charge',
                                billingFrequency: 12,
                                autoBilled: true
                            },
                            // WEEKLY
                            {
                                displayName: 'Automatic Weekly Charge',
                                billingFrequency: 52,
                                autoBilled: true
                            },
                            // DAILY
                            {
                                displayName: 'Automatic Daily Charge',
                                billingFrequency: 365,
                                autoBilled: true
                            },
                            // ONE_TIME
                            {
                                displayName: 'One-time charge',
                                billingFrequency: 1,
                                autoBilled: true
                            },
                            // SUPPORT_YEARLY
                            {
                                displayName: 'Support will collect payment yearly.',
                                billingFrequency: 2023,
                                autoBilled: false
                            },
                            // SUPPORT_QUARTERLY
                            {
                                displayName: 'Support will collect the payment quarterly.',
                                billingFrequency: 4,
                                autoBilled: false
                            },
                            // SUPPORT_MONTHLY
                            {
                                displayName: 'Support will collect payment monthly.',
                                billingFrequency: 12,
                                autoBilled: false
                            },
                            // SUPPORT_WEEKLY
                            {
                                displayName: 'Support will collect the payment weekly.',
                                billingFrequency: 52,
                                autoBilled: false
                            },
                            // SUPPORT_DAILY
                            {
                                displayName: 'Support will collect the payment daily.',
                                billingFrequency: 365,
                                autoBilled: false
                            },
                            // SUPPORT_ONE_TIME
                            {
                                displayName: 'Support will collect a one-time payment.',
                                billingFrequency: 1,
                                autoBilled: false
                            },
                            // SUPPORT
                            {
                                displayName: 'Support will contact when payment needs made.',
                                billingFrequency: 0,
                                autoBilled: false
                            }
                        ]);
                });
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
